/* eslint-disable max-len */
import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import Spinner from '../../ui/Spinner';
import { ShareChannel } from '../../../modules/share';
import {
  BodyText,
  HeaderText,
  Stack,
  Touchable,
  Absolute,
  Button,
} from '../../ui';
import CopyButton from '../../ui/CopyButton';
import { AnimatedShakeView } from '../../ui/AnimatedShakeView';
import { BasicCheckMarkIcon, GiftBoxIcon } from '../../ui/Icons';
import { COPY_LINK } from './constants';
import { useIsMobile } from '../../../hooks';
import analytics from '../../../modules/analytics';

const DefaultCopied = () => (
  <Box alignItems="center">
    <Inline paddingX={8} space={4} flex={1}>
      <Box borderRadius={50} color={colors.green} padding={4}>
        <BasicCheckMarkIcon color={colors.whiteWash} height={12} width={12} />
      </Box>
      <BodyText color={colors.whiteWash}>Link Copied</BodyText>
    </Inline>
  </Box>
);

type ShareMenuContentProps = {
  channels: Array<ShareChannel>;
  loading: boolean;
  onDetailsPress: () => void;
  copyableShareLink: string;
  performDirectShare?: () => void;
  onShareSelect?: (id: ShareChannel) => void;
  onShareComplete?: (id: ShareChannel) => void;
  closeShareModal: () => void;
};

/**
 * Displays a list of Share options for the specified channels.
 */
const ShareMenuContent: React.FCWithChildren<ShareMenuContentProps> = ({
  channels,
  loading,
  onDetailsPress,
  copyableShareLink,
  performDirectShare,
  onShareSelect,
  onShareComplete,
  closeShareModal,
}) => {
  const isMobile = useIsMobile();

  const handleCopyLinkAndShowSuccessModal = () => {
    onShareSelect?.(COPY_LINK as ShareChannel);
    setTimeout(() => {
      analytics.track('client_profile_share_modal_cta_clicked', {
        native_share: false,
      });
      onShareComplete?.(COPY_LINK as ShareChannel);
      closeShareModal();
    }, 3000);
  };

  const detailsButton = React.useMemo(() => (
    <Stack alignItems="center">
      <Touchable
        onClick={onDetailsPress}
        testID="client-share-details-open-btn"
      >
        <Box
          borderBottomColor={colors.blackInBlack}
          borderBottomWidth={0.5}
        >
          <BodyText>Details</BodyText>
        </Box>
      </Touchable>
    </Stack>
  ), [onDetailsPress]);

  return (
    <Box padding={16} paddingTop={48} flex={1}>
      <Stack alignItems="center">
        <Box paddingBottom={24}>
          <AnimatedShakeView>
            <GiftBoxIcon width={isMobile ? 100 : 68} height={isMobile ? 100 : 68} />
          </AnimatedShakeView>
        </Box>
        <HeaderText textAlign="center" testID="share-menu-header-text-testid" level={1}>{'$50 for you,\n$50 for a friend!'}</HeaderText>
        <Box paddingBottom={24} paddingTop={8}>
          {isMobile ? (
            <BodyText testID="share-menu-body-text-testid" textAlign="center">Share StyleSeat with friends and you both receive 15% off (up to $50) when you discover and book{'\n'}with a new pro. {detailsButton}</BodyText>
          ) : (
            <BodyText testID="share-menu-body-text-testid" textAlign="center">
              Share StyleSeat with friends and you both receive
              {'\n'}
              15% off (up to $50) when you discover and book
              {'\n'}
              with a new pro.
            </BodyText>
          )}
        </Box>
      </Stack>

      <Spinner show={loading || !channels} />
      <Absolute left={24} bottom={24} right={24}>
        {/* open native share if possible, else just copy to clipboard */}
        {performDirectShare
          ? (
            <Button
              onClick={performDirectShare}
              text="Share Link"
              appearance="primary-neue"
            />
          ) : (
            <CopyButton
              copyText={copyableShareLink}
              text={null}
              onCopied={handleCopyLinkAndShowSuccessModal}
              testID="client_proprofile_share_card_url_copy_button"
              appearance="primary-neue"
              Copied={() => <DefaultCopied />}
              style={{ width: '100%', height: 40 }}
            >
              <Box paddingX={8}><BodyText color={colors.whiteWash}>Share Link</BodyText></Box>
            </CopyButton>
          )}
        {!isMobile && detailsButton}
      </Absolute>
    </Box>
  );
};

export default ShareMenuContent;
